import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface UserEntry {
  id: string;
  name: string;
  email: string;
  groupId: string;
  forms?: Array<{ form_id: number; completed: boolean }>;
}

interface FormOption {
  id: number;
  name: string;
}

interface AssignFormsProps {
  users: UserEntry[];
  uid: string;
  onClose: () => void;
}

const AssignForms: React.FC<AssignFormsProps> = ({ users, uid, onClose }) => {
  const [availableForms, setAvailableForms] = useState<FormOption[]>([]);
  const [selectedFormIds, setSelectedFormIds] = useState<Set<number>>(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const fetchForms = async () => {
      const snap = await getDoc(doc(db, 'forms', 'structures'));
      if (!snap.exists()) return;
      const data = snap.data() as Record<string, { name: string }>;
      const opts = Object.entries(data)
        .map(([id, s]) => ({ id: Number(id), name: s.name }))
        .sort((a, b) => a.id - b.id);
      setAvailableForms(opts);
    };
    fetchForms();
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGroup = !groupFilter || user.groupId?.toLowerCase().includes(groupFilter.toLowerCase());
    return matchesSearch && matchesGroup;
  });

  const toggleUser = (id: string) =>
    setSelectedUserIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleForm = (id: number) =>
    setSelectedFormIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSelectAllUsers = () =>
    selectedUserIds.size === filteredUsers.length
      ? setSelectedUserIds(new Set())
      : setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));

  const handleAssign = async () => {
    if (selectedUserIds.size === 0 || selectedFormIds.size === 0) {
      setStatus('Select at least one user and one form.');
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      for (const userId of Array.from(selectedUserIds)) {
        const snap = await getDoc(doc(db, 'users', userId));
        const existing: Array<{ form_id: number; completed: boolean }> =
          snap.exists() ? (snap.data().forms ?? []) : [];
        const existingIds = new Set(existing.map(f => f.form_id));
        const toAdd = Array.from(selectedFormIds)
          .filter(id => !existingIds.has(id))
          .map(id => ({ form_id: id, completed: false }));
        if (toAdd.length > 0) {
          await updateDoc(doc(db, 'users', userId), { forms: [...existing, ...toAdd] });
        }
      }
      setStatus('Forms assigned.');
      setTimeout(onClose, 700);
    } catch {
      setStatus('Error assigning forms.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white h-5/6 w-11/12 lg:w-3/4 xl:w-2/3 p-8 rounded-3xl shadow-2xl relative overflow-auto">
        <button className="btn btn-sm btn-circle absolute right-2 top-2" onClick={onClose}>✕</button>
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">Assign Forms</h2>

        <div className="bg-blue-50 border border-blue-300 text-blue-700 p-3 rounded-md mb-6 text-sm">
          Select users and the forms to assign. Already-assigned forms are skipped.
        </div>

        <div className="flex flex-wrap lg:flex-nowrap gap-6">
          {/* User list */}
          <div className="lg:w-2/3">
            <input type="text" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
              placeholder="Filter by group" className="input input-bordered w-full mb-3" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search users" className="input input-bordered w-full mb-3" />
            <button onClick={handleSelectAllUsers} className="btn btn-primary btn-sm mb-3 w-full">
              {selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
            <div className="overflow-y-auto max-h-96 bg-gray-50 p-4 rounded-lg shadow-inner">
              <ul className="space-y-3">
                {filteredUsers.map(user => (
                  <li key={user.id} className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3">
                    <input type="checkbox" checked={selectedUserIds.has(user.id)}
                      onChange={() => toggleUser(user.id)} className="checkbox checkbox-primary mt-0.5" />
                    <div>
                      <span className="block font-medium text-gray-800">{user.name}</span>
                      <span className="block text-sm text-gray-500">{user.email}</span>
                      <span className="block text-xs text-gray-400">Group: {user.groupId}</span>
                    </div>
                  </li>
                ))}
                {filteredUsers.length === 0 && (
                  <li className="text-sm text-gray-400 px-2">No users found.</li>
                )}
              </ul>
            </div>
          </div>

          {/* Form selection */}
          <div className="lg:w-1/3 bg-gray-100 p-4 rounded-lg shadow-inner">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Forms</h3>
            {availableForms.length === 0 ? (
              <p className="text-sm text-gray-400">No forms available.</p>
            ) : (
              <div className="space-y-2 mb-6">
                {availableForms.map(form => (
                  <label key={form.id} className="flex items-center gap-3 cursor-pointer bg-white rounded-lg p-3 shadow-sm">
                    <input type="checkbox" checked={selectedFormIds.has(form.id)}
                      onChange={() => toggleForm(form.id)} className="checkbox checkbox-primary" />
                    <span className="text-sm text-gray-800">
                      <span className="text-gray-400 mr-1">#{form.id}</span>{form.name}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {status && (
              <p className={`text-sm mb-3 ${status.startsWith('Error') || status.startsWith('Select') ? 'text-red-500' : 'text-green-600'}`}>
                {status}
              </p>
            )}

            <button
              onClick={handleAssign}
              disabled={loading || selectedUserIds.size === 0 || selectedFormIds.size === 0}
              className="btn btn-primary w-full"
            >
              {loading ? 'Assigning…' : 'Assign Forms'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignForms;
